import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { getRoles } from '../../services/roles';

const permissionGroups = {
  orders: ['orders_create', 'orders_read', 'orders_update', 'orders_delete'],
  drivers: ['drivers_create', 'drivers_read', 'drivers_update', 'drivers_delete'],
  companies: ['companies_create', 'companies_read', 'companies_update', 'companies_delete'],
  affiliates: ['affiliates_create', 'affiliates_read', 'affiliates_update', 'affiliates_delete'],
  services: ['services_create', 'services_read', 'services_update', 'services_delete'],
  blog: ['blog_create', 'blog_read', 'blog_update', 'blog_delete'],
  tour_guides: ['tour_guides_create', 'tour_guides_read', 'tour_guides_update', 'tour_guides_delete'],
  settings: ['settings_read', 'settings_update'],
  users: ['users_create', 'users_read', 'users_update', 'users_delete'],
  coupons: ['coupons_create', 'coupons_read', 'coupons_update', 'coupons_delete'],
  reviews: ['reviews_create', 'reviews_read', 'reviews_update', 'reviews_delete'],
  notifications: ['notifications_create', 'notifications_read', 'notifications_update', 'notifications_delete'],
  reports: ['reports_view', 'reports_export'],
  system: ['system_read'],
  backup: ['backup_create', 'backup_read', 'backup_restore', 'backup_delete'],
};

export default function UserForm({ isOpen, onClose, onSubmit, initialData }) {
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role_id: '',
    permissions: [],
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [customPermissions, setCustomPermissions] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        username: initialData.username || '',
        email: initialData.email || '',
        password: '',
        full_name: initialData.full_name || '',
        phone: initialData.phone || '',
        role_id: initialData.role?.id || initialData.role_id || '',
        permissions: initialData.permissions || [],
        is_active: initialData.is_active !== undefined ? initialData.is_active : true,
      });
      setCustomPermissions(initialData.permissions?.length > 0);
    }
  }, [initialData]);

  const fetchRoles = async () => {
    try {
      const data = await getRoles();
      setRoles(data.roles || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setLoadingRoles(false);
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleRoleChange = (roleId) => {
    const selectedRole = roles.find(r => r._id === roleId);
    if (selectedRole && !customPermissions) {
      setFormData({
        ...formData,
        role_id: roleId,
        permissions: selectedRole.permissions?.map(p => p.code) || [],
      });
    } else {
      setFormData({ ...formData, role_id: roleId });
    }
  };

  const handlePermissionToggle = (permission) => {
    const newPermissions = formData.permissions.includes(permission)
      ? formData.permissions.filter(p => p !== permission)
      : [...formData.permissions, permission];
    setFormData({ ...formData, permissions: newPermissions });
    setCustomPermissions(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // التأكد من وجود role_id
    if (!formData.role_id) {
      toast.error('Please select a role');
      setLoading(false);
      return;
    }
    
    await onSubmit(formData);
    setLoading(false);
    onClose();
  };

  const selectedRole = roles.find(r => r._id === formData.role_id);
  const defaultPermissions = selectedRole?.permissions?.map(p => p.code) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">{initialData ? 'Edit User' : 'Add New User'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            {!initialData && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!initialData}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="flex items-center mt-6">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
            {loadingRoles ? (
              <div className="text-gray-500">Loading roles...</div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {roles.map(role => (
                  <label
                    key={role._id}
                    className={`p-3 border rounded-lg cursor-pointer transition ${
                      formData.role_id === role._id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role._id}
                      checked={formData.role_id === role._id}
                      onChange={() => handleRoleChange(role._id)}
                      className="mr-2"
                    />
                    <div>
                      <p className="font-medium text-gray-800">{role.name}</p>
                      <p className="text-xs text-gray-500">{role.description || 'No description'}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Custom Permissions */}
          {formData.role_id && selectedRole && selectedRole.code !== 'super_admin' && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-medium text-gray-700">Permissions</label>
                <button
                  type="button"
                  onClick={() => setCustomPermissions(!customPermissions)}
                  className="text-xs text-primary-500 hover:text-primary-600"
                >
                  {customPermissions ? 'Use Role Default' : 'Customize Permissions'}
                </button>
              </div>
              
              <div className="space-y-4">
                {Object.entries(permissionGroups).map(([group, permissions]) => (
                  <div key={group} className="border rounded-lg p-3">
                    <p className="font-medium text-gray-700 capitalize mb-2">{group.replace('_', ' ')}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {permissions.map(perm => (
                        <label key={perm} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={formData.permissions.includes(perm)}
                            onChange={() => handlePermissionToggle(perm)}
                            disabled={!customPermissions}
                            className="mr-2"
                          />
                          <span className="capitalize">{perm.replace(/_/g, ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.role_id}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition"
            >
              {loading ? 'Saving...' : initialData ? 'Update User' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
